import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch, TextField,
} from '@mui/material';
import { APIURL } from '../../config';

const initialValues = {
  residence_name: '',
  loc_id: '',
  no_of_floors: 1,
  has_elevators: false,
};

const locationLabel = (location) => [
  location.loc_addr_line1,
  location.loc_addr_line2,
  location.loc_city,
  location.loc_state,
  location.loc_zip,
].filter(Boolean).join(', ');

const ResidenceForm = ({ open, onClose, onSave, residence, user }) => {
  const [values, setValues] = useState(initialValues);
  const [locations, setLocations] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setValues(residence ? {
      residence_name: residence.residence_name || '',
      loc_id: residence.loc_id || '',
      no_of_floors: residence.no_of_floors || 1,
      has_elevators: !!residence.has_elevators,
    } : initialValues);
    setError('');
    axios.get(`${APIURL}/location`, { params: { comp_id: user.comp_id } })
      .then(({ data }) => setLocations(Array.isArray(data) ? data : []))
      .catch(() => setLocations([]));
  }, [open, residence, user.comp_id]);

  const update = (field, value) => setValues((current) => ({ ...current, [field]: value }));

  const handleSave = async () => {
    if (!values.residence_name.trim()) {
      setError('Residence name is required.');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      residence_name: values.residence_name.trim(),
      loc_id: values.loc_id || null,
      no_of_floors: Number(values.no_of_floors),
      has_elevators: values.has_elevators,
      company_id: user.comp_id,
      emp_id: user.emp_id,
    };
    try {
      if (residence) {
        await axios.put(`${APIURL}/residences/${residence.residence_id}`, {
          ...payload,
          residence_id: residence.residence_id,
        });
      } else {
        await axios.post(`${APIURL}/residences`, payload);
      }
      await onSave();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to save the residence.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{residence ? 'Edit Residence' : 'New Residence'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            required
            label="Residence Name"
            value={values.residence_name}
            onChange={(event) => update('residence_name', event.target.value)}
            placeholder="e.g., Maple Street Residence"
          />
          <FormControl fullWidth>
            <InputLabel>Address</InputLabel>
            <Select value={values.loc_id} label="Address" onChange={(event) => update('loc_id', event.target.value)}>
              <MenuItem value=""><em>No address</em></MenuItem>
              {locations.filter((location) => location.loc_is_active !== false && location.loc_is_active !== 0).map((location) => (
                <MenuItem key={location.loc_id} value={location.loc_id}>{locationLabel(location)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            required
            type="number"
            label="Number of Floors"
            value={values.no_of_floors}
            onChange={(event) => update('no_of_floors', Math.max(1, Number(event.target.value)))}
            inputProps={{ min: 1 }}
          />
          <FormControlLabel
            control={<Switch checked={values.has_elevators} onChange={(event) => update('has_elevators', event.target.checked)} />}
            label="Residence has elevators"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || !values.residence_name.trim()}>
          {saving ? 'Saving…' : 'Save Residence'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResidenceForm;
